import axios from 'axios';
import { API_BASE_URL, SERVER_BASE_URL } from '../config';
import { addCSRFTokenToHeaders, resetCSRFToken, initCSRF } from './csrf';

// Экспортируем для обратной совместимости
export { SERVER_BASE_URL };

// Функция для формирования URL изображения
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (typeof imagePath !== 'string') return null;
  // data:/blob: — не префиксовать доменом (иначе https://site/data:image... и ломает HTTP/2)
  if (imagePath.startsWith('data:') || imagePath.startsWith('blob:')) {
    // #region agent log
    fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'27ed5c'},body:JSON.stringify({sessionId:'27ed5c',location:'api.js:getImageUrl',message:'pass-through data/blob',data:{prefix:imagePath.slice(0,32),len:imagePath.length},timestamp:Date.now(),hypothesisId:'C',runId:'post-fix'})}).catch(()=>{});
    // #endregion
    return imagePath;
  }
  // Уже поломанный URL вида https://domain/data:image... — вытащить чистый data:
  const brokenDataIdx = imagePath.indexOf('/data:');
  if (brokenDataIdx !== -1) {
    // #region agent log
    fetch('http://127.0.0.1:7515/ingest/1206fec0-b699-4bb3-b443-a818513e7935',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'27ed5c'},body:JSON.stringify({sessionId:'27ed5c',location:'api.js:getImageUrl',message:'repaired broken /data: url',data:{prefix:imagePath.slice(0,48)},timestamp:Date.now(),hypothesisId:'C',runId:'post-fix'})}).catch(()=>{});
    // #endregion
    return imagePath.slice(brokenDataIdx + 1);
  }
  if (imagePath.startsWith('http')) return imagePath; // Уже полный URL
  const normalized = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${SERVER_BASE_URL}${normalized}`;
};

// Создание экземпляра axios с базовыми настройками
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 сек — запросы с отправкой email (forgot-password, register) могут занимать время
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена к запросам
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Добавляем CSRF токен для изменяющих запросов
    const changingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    if (changingMethods.includes(config.method.toUpperCase())) {
      try {
        const headers = await addCSRFTokenToHeaders(config.headers || {});
        config.headers = { ...config.headers, ...headers };
      } catch (error) {
        console.error('Error adding CSRF token:', error);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Флаг для предотвращения бесконечных попыток обновления токена
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Интерцептор для обработки ответов
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Обработка ошибки CSRF (403)
    if (error.response?.status === 403 && error.response?.data?.message?.includes('CSRF')) {
      console.warn('CSRF token invalid, resetting...');
      resetCSRFToken();
      
      // Пытаемся обновить токен и повторить запрос
      try {
        await initCSRF();
        const config = error.config;
        const changingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
        if (changingMethods.includes(config.method.toUpperCase())) {
          const headers = await addCSRFTokenToHeaders(config.headers || {});
          config.headers = { ...config.headers, ...headers };
          return api.request(config);
        }
      } catch (refreshError) {
        console.error('Failed to refresh CSRF token:', refreshError);
      }
    }
    
    // БЕЗОПАСНОСТЬ: Если токен недействителен (401), пытаемся обновить через refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Если уже идет обновление, добавляем запрос в очередь
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api.request(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        // Нет refresh токена - выход
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        processQueue(new Error('No refresh token'), null);
        isRefreshing = false;
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Пытаемся обновить токен
        const response = await api.post('/auth/refresh', { refreshToken });
        
        if (response.data.success && response.data.token) {
          const newToken = response.data.token;
          localStorage.setItem('token', newToken);
          
          // Обновляем токен в запросе
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          
          // Обрабатываем очередь
          processQueue(null, newToken);
          isRefreshing = false;
          
          // Повторяем оригинальный запрос
          return api.request(originalRequest);
        } else {
          throw new Error('Invalid refresh response');
        }
      } catch (refreshError) {
        // Ошибка обновления токена - выход
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        processQueue(refreshError, null);
        isRefreshing = false;
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // Обработка других ошибок (401 без возможности обновления)
    if (error.response?.status === 401 && originalRequest._retry) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Обработка таймаута
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.error('Таймаут запроса:', error.config?.url || 'unknown endpoint');
      // Не перенаправляем при таймауте, чтобы пользователь мог повторить попытку
    }
    
    // Обработка ошибок сервера
    if (error.response?.status >= 500) {
      console.error('Ошибка сервера:', error.response.data?.message || error.message);
    }
    
    // Обработка сетевых ошибок
    if (!error.response && error.request) {
      console.error('Сетевая ошибка - сервер не отвечает. Проверьте, запущен ли backend сервер.');
    }
    
    return Promise.reject(error);
  }
);

// Функции для различных типов запросов
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
};

export const productsAPI = {
  getAll: (params = {}) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getByCategory: (categoryId) => api.get(`/products/category/${categoryId}`),
  search: (query) => api.get('/products/search', { params: { q: query } }),
};

export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
};

export const ordersAPI = {
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (orderData) => api.post('/orders', orderData),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
};

export const cartAPI = {
  getItems: () => api.get('/cart'),
  addItem: (productId, quantity) => api.post('/cart', { productId, quantity }),
  updateItem: (productId, quantity) => api.put('/cart', { productId, quantity }),
  removeItem: (productId) => api.delete(`/cart/${productId}`),
  clear: () => api.delete('/cart'),
};

export const favoritesAPI = {
  getAll: () => api.get('/users/favorites'),
  add: (productId) => api.post('/users/favorites', { productId }),
  remove: (productId) => api.delete(`/users/favorites/${productId}`),
};

export const usersAPI = {
  getStats: () => api.get('/users/stats'),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
};

export const notificationsAPI = {
  getRecent: async () => {
    const response = await api.get('/notifications/recent');
    return response.data;
  },
  getList: async (params = {}) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  },
  markRead: async (id) => {
    const response = await api.post(`/notifications/${id}/read`);
    return response.data;
  },
  markAllRead: async () => {
    const response = await api.post('/notifications/mark-all-read');
    return response.data;
  },
  send: async (data) => {
    const response = await api.post('/notifications/send', data);
    return response.data;
  },
};

// Функция для обработки ошибок API
export const handleAPIError = (error) => {
  if (error.response) {
    // Ошибка с ответом от сервера
    const message = error.response.data?.message || 'Произошла ошибка';
    const status = error.response.status;
    
    switch (status) {
      case 400:
        return { message: `Неверные данные: ${message}`, type: 'validation' };
      case 401:
        return { message: 'Необходима авторизация', type: 'auth' };
      case 403:
        return { message: 'Доступ запрещен', type: 'permission' };
      case 404:
        return { message: 'Ресурс не найден', type: 'notFound' };
      case 500:
        return { message: 'Ошибка сервера', type: 'server' };
      default:
        return { message, type: 'unknown' };
    }
  } else if (error.request) {
    // Ошибка сети
    return { message: 'Нет соединения с сервером', type: 'network' };
  } else {
    // Другие ошибки
    return { message: 'Произошла неизвестная ошибка', type: 'unknown' };
  }
};

// Функция для загрузки файлов
export const uploadFile = async (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        if (onProgress) {
          onProgress(percentCompleted);
        }
      },
    });
    
    return response.data;
  } catch (error) {
    throw handleAPIError(error);
  }
};

// Универсальная функция для API запросов
export const apiRequest = async (endpoint, options = {}) => {
  try {
    const { method = 'GET', data, params, ...restOptions } = options;
    
    const config = {
      method,
      url: endpoint,
      ...restOptions
    };
    
    if (data) {
      config.data = data;
    }
    
    if (params) {
      config.params = params;
    }
    
    const response = await api(config);
    return response.data;
  } catch (error) {
    throw handleAPIError(error);
  }
};

export default api; 